import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import {
  fetchInsRecentUserSub,
  fetchOrderSubsListRequest,
  insRecentUserSubAction,
  orderResumeRequest,
  orderSubsCancelRequest,
} from '@/lib/service';
import { OrderInfoModel, OrderModel, RecentSubStatusModel } from '@/types/home';
import { Button, Dropdown as AntdDropdown, message, Modal, Radio, RadioChangeEvent, Space, Spin } from 'antd';
import { debounce } from 'lodash-es';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import classNames from 'classnames';
import { useUserProvider } from '@/context/UserProvider';
import SettingsEmptyContent from '../SettingsEmpty';
import Image from 'next/image';
import dayjs from 'dayjs';

const SubscriptionDetailContent: React.FC = () => {
  const { isLogin } = useUserProvider();
  const { t } = useTranslation();
  const [orderList, setOrderList] = useState<OrderInfoModel[]>([]);
  const [storyOrderList, setStoryOrderList] = useState<OrderInfoModel[]>([]);
  const [selectRow, setSelectRow] = useState<any>({});
  const [openUnsubscribeStop, toggleUnsubscribeStop] = useState<OrderInfoModel>();
  const [reasonVal, setReasonVal] = useState<string>(null);
  const [foundReasonStr, setFoundReasonStr] = useState<string>(null);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [openUnsubscribe, toggleUnsubscribe] = useState<OrderInfoModel>();
  const [currentStatus, setCurrentStatus] = useState<RecentSubStatusModel>(null);
  const [loading, setLoading] = useState(false);
  const [checked1, setChecked1] = useState(false);
  const [checked2, setChecked2] = useState(false);

  const typeList: {
    type:
      | 'main'
      | 'story'
      | 'recent_follow'
      | 'hash_tag'
      | 'unfollow'
      | 'post_comment'
      | 'follow_export'
      | 'email_finder';
    name: string;
    img: string;
    link: string;
  }[] = useMemo(() => {
    return [
      {
        type: 'main',
        name: t('Social Insights'),
        img: '/images/settings/ins_activity_monitor.webp',
        link: '/dashboard',
      },
      {
        type: 'post_comment',
        name: t('Post Comments'),
        img: '/images/settings/ins_activity_monitor.webp',
        link: '/post-comments',
      },
      {
        type: 'story',
        name: t('Instagram Viewer & Downloader'),
        img: '/images/toolkit/toolkit-icon2.png',
        link: '/download-from-instagram',
      },
      {
        type: 'recent_follow',
        name: t('Recent Follows/Followers Tracking'),
        img: '/images/settings/ins_viewer_downloader.webp',
        link: '/instagram-new-follow-tracker',
      },
      {
        type: 'unfollow',
        name: t('Instagram Unfollower Tracker'),
        img: '/images/toolkit/toolkit6.webp',
        link: '/instagram-unfollowers-tracker',
      },
      {
        type: 'follow_export',
        name: t('Instagram Follower Exporter'),
        img: '/images/toolkit/toolkit7.webp',
        link: '/ig-follower-export-tool',
      },
      {
        type: 'email_finder',
        name: t('Instagram Email Finder'),
        img: '/images/toolkit/toolkit7.webp',
        link: '/email-finder',
      },
    ];
  }, []);

  const [serviceType, setServiceType] = useState(typeList[0]);

  const statusList = useMemo(() => {
    return [
      {
        type: '0',
        name: t('All'),
      },
      {
        type: '1',
        name: t('Active'),
      },
      {
        type: '2',
        name: t('Cancelled'),
      },
    ];
  }, []);

  const [statusType, setStatusType] = useState(statusList[0]);

  const loadData = () => {
    setLoading(true);

    if (serviceType?.type !== 'recent_follow') {
      fetchOrderSubsListRequest(serviceType?.type, null)
        .then((res) => {
          if (res.code === 0) {
            if (serviceType?.type === 'story') {
              setStoryOrderList(res?.data ?? []);
            } else {
              setOrderList(res?.data ?? []);
            }
          } else {
            if (serviceType?.type === 'story') {
              setStoryOrderList([]);
            } else {
              setOrderList([]);
            }
            message.error(res?.message);
          }
        })
        .catch((error) => {
          if (serviceType?.type === 'story') {
            setStoryOrderList([]);
          } else {
            setOrderList([]);
          }
          message.error(error?.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      fetchInsRecentUserSub()
        .then((res) => {
          if (res.code === 0) {
            setCurrentStatus(res?.data);
          } else {
            setCurrentStatus(null);
            message.error(res?.message);
          }
        })
        .catch((error) => {
          setCurrentStatus(null);
          message.error(error?.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const loadSubList = useCallback(
    debounce(() => {
      loadData();
    }, 300),
    []
  );

  const getShowList = useMemo(() => {
    console.log('getShowList statusType', statusType?.type);
    console.log('getShowList serviceType', serviceType?.type);
    console.log('getShowList currentStatus', currentStatus);

    // 处理Recent Follow类型的特殊情况
    if (serviceType?.type === 'recent_follow') {
      if (statusType?.type === '0') {
        return currentStatus && currentStatus?.sub_status !== 'unsub'
          ? [
              {
                media_name: '',
                status: currentStatus?.sub_status,
                expire_time: currentStatus?.expired_time,
                data_type: currentStatus?.item_name,
              },
            ]
          : [];
      } else if (statusType?.type === '1') {
        return currentStatus?.sub_status === 'normal'
          ? [
              {
                media_name: null,
                status: currentStatus?.sub_status,
                expire_time: currentStatus?.expired_time,
                data_type: currentStatus?.item_name,
              },
            ]
          : [];
      } else if (statusType?.type === '2') {
        return currentStatus?.sub_status === 'cancel'
          ? [
              {
                media_name: null,
                status: currentStatus?.sub_status,
                expire_time: currentStatus?.expired_time,
                data_type: currentStatus?.item_name,
              },
            ]
          : [];
      }
      return [];
    }

    // 根据服务类型选择数据来源
    const dataSource = serviceType?.type === 'story' ? storyOrderList : orderList;

    // 根据状态类型过滤数据
    if (statusType?.type === '0') {
      return dataSource;
    } else if (statusType?.type === '1') {
      return dataSource?.filter((item) => item?.status === 'normal');
    } else if (statusType?.type === '2') {
      const statusToFilter = serviceType?.type === 'unfollow' ? 'expire' : 'cancel';
      return dataSource?.filter((item) => item?.status === statusToFilter);
    }

    return [];
  }, [statusType?.type, serviceType?.type, currentStatus, orderList, storyOrderList]);

  useEffect(() => {
    if (isLogin) {
      loadSubList();
    }
  }, [isLogin]);

  useEffect(() => {
    if (isLogin) {
      loadData();
    }
  }, [serviceType?.type]);

  const onUnsubscribeClick = () => {
    const str = JSON.stringify({
      reasonVal: reasonVal,
      foundReasonStr: Number(reasonVal) === 1 ? foundReasonStr : null,
    });

    setUnsubscribeLoading(true);
    if (serviceType?.type === 'recent_follow') {
      insRecentUserSubAction(false, null, str)
        .then((res) => {
          if (res.code === 0) {
            message.success(t('UnsubscribeSuccess'));
            loadData();
          } else {
            message.error(res?.message);
          }
          setUnsubscribeLoading(false);
          toggleUnsubscribe(undefined);
        })
        .catch((error) => {
          message.error(error.message);
          setUnsubscribeLoading(false);
        });

      return;
    }

    orderSubsCancelRequest(str, openUnsubscribe?.subs_id, null)
      .then((res) => {
        if (res.code === 0) {
          message.success(t('UnsubscribeSuccess'));
          // setOrderInfoModel(undefined)
          loadData();
        } else {
          message.error(res?.message);
        }
        setUnsubscribeLoading(false);
        toggleUnsubscribe(undefined);
      })
      .catch((error) => {
        message.error(error.message);
        setUnsubscribeLoading(false);
      });
  };

  const loadScripePay = async (orderModel: OrderModel) => {
    window.open(orderModel?.pay_link, '_self');
  };

  const onResubscribeClick = (itemId: any) => {
    if (serviceType?.type === 'recent_follow') {
      insRecentUserSubAction(true, '/settings')
        .then((res) => {
          if (res.code === 0) {
            if (res?.data?.pay_link) {
              loadScripePay(res?.data);
              return;
            }
            message.success(t('Re-subscribeSuccess'));
            loadData();
          } else {
            message.error(res?.message);
          }
        })
        .catch((error) => {
          message.error(error.message);
        });
      return;
    }

    orderResumeRequest(itemId, null, '/dashboard?dashboard_type=track')
      .then((res) => {
        if (res.code === 0) {
          if (res?.data?.pay_link) {
            loadScripePay(res?.data);
            return;
          }
          message.success(t('Re-subscribeSuccess'));
          loadData();
        } else {
          message.error(res?.message);
        }
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  const unsubscribeContentStop = () => {
    console.log(serviceType);

    const isCoommentEmail = serviceType?.type == 'email_finder' || serviceType?.type == 'post_comment';
    return (
      <Modal
        className={styles.unsubscribeModalContainer}
        // style={{ padding: '20px 24px' }}
        open={openUnsubscribeStop != undefined}
        onCancel={() => toggleUnsubscribeStop(undefined)}
        destroyOnHidden={true}
        footer={[
          <div key={'footer'} className={styles.footerContainer} style={{ textAlign: 'center', paddingBottom: '20px' }}>
            <Button className={styles.btn} onClick={() => toggleUnsubscribeStop(undefined)}>
              {t('Keep My Plan')}
            </Button>
            <Button
              className={classNames(styles.btn, styles.btnNew)}
              key="submit"
              disabled={isCoommentEmail ? !checked1 || !checked2 : !checked1}
              loading={unsubscribeLoading}
              onClick={() => {
                toggleUnsubscribeStop(undefined);
                let timer = setTimeout(() => {
                  toggleUnsubscribe(selectRow);
                  clearTimeout(timer);
                }, 0);
              }}
            >
              {t('Cancel Anyway')}
            </Button>
          </div>,
        ]}
      >
        <div className={styles.titleContainer} style={{ textAlign: 'center', padding: '20px 24px 0 24px' }}></div>
        <div className="p-6 pt-0">
          <div className="text-xl font-bold mb-2">{t('Ready to Cancel?')}</div>
          <div className="text-gray-500 mb-4">{t('Please confirm below:')}</div>
          <div className="flex items-center mb-3 cursor-pointer select-none" onClick={() => setChecked1(!checked1)}>
            <input
              type="checkbox"
              id="cancel-offer"
              checked={checked1}
              onChange={() => setChecked1(!checked1)}
              className="hidden"
            />
            <span
              className={`
                w-4 h-4 mr-2 flex items-center justify-center border rounded flex-shrink-0
                ${checked1 ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-400'}
                transition-colors
              `}
            >
              {checked1 && (
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <label htmlFor="cancel-offer" className="text-base font-medium cursor-pointer">
              {t('I understand this offer may no longer be available if I cancel now.')}
            </label>
          </div>
          {isCoommentEmail && (
            <div className="flex items-center mb-3 cursor-pointer select-none" onClick={() => setChecked2(!checked2)}>
              <input
                type="checkbox"
                id="cancel-offer1"
                checked={checked2}
                onChange={() => setChecked2(!checked2)}
                className="hidden"
              />
              <span
                className={`
                w-4 h-4 mr-2 flex items-center justify-center border rounded flex-shrink-0
                ${checked2 ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-400'}
                transition-colors
              `}
              >
                {checked2 && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <label htmlFor="cancel-offer1" className="text-base font-medium cursor-pointer">
                {t('I understand my remaining credits stay active until the end of my billing cycle.')}
              </label>
            </div>
          )}

          <div className="text-sm mt-4">
            <span className="text-red-500">*</span>
            <span className="text-gray-500 ml-1">{t("Heads up — once this deal's gone, it's gone for good.")}</span>
          </div>
        </div>
      </Modal>
    );
  };

  const unsubscribeContent = () => {
    return (
      <Modal
        className={styles.unsubscribeModalContainer}
        open={openUnsubscribe != undefined}
        onCancel={() => toggleUnsubscribe(undefined)}
        destroyOnHidden={true}
        footer={[
          <div key={'footer'} className={styles.footerContainer} style={{ paddingBottom: '20px' }}>
            <Button className={styles.btn} onClick={() => toggleUnsubscribe(undefined)}>
              {t('Keep My Plan')}
            </Button>
            <Button
              className={styles.btn}
              key="submit"
              loading={unsubscribeLoading}
              onClick={() => onUnsubscribeClick()}
            >
              {t('Cancel Anyway')}
            </Button>
          </div>,
        ]}
      >
        <div style={{ padding: '20px 24px 0 24px' }}>
          <div className={styles.titleContainer} style={{ textAlign: 'center' }}>
            {t('ShareYourReasonForLeaving')}
            <p>{t('YourFeedbackMatters_Desc')}</p>
          </div>
          <Radio.Group defaultValue={reasonVal} onChange={(e: RadioChangeEvent) => setReasonVal(e.target.value)}>
            <Space direction="vertical">
              <Radio value={1}>{t('FoundAnAlternativeService')}</Radio>

              <Radio value={2}>{t('FeaturesDontMeetMyNeeds')}</Radio>
              <Radio value={3}>{t('ServiceIsTooComplex')}</Radio>
              <Radio value={4}>{t('TooExpensive')}</Radio>
              <Radio value={5}>{t('ServiceQualityDidntMeetExpectations')}</Radio>
              <Radio value={6}>{t('ConcernsAboutPrivacyOrDataSecurity')}</Radio>
              <Radio value={7}>{t('PersonalReasons')}</Radio>
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    );
  };

  const typeItems = typeList?.map((item) => {
    return {
      key: item?.type,
      label: <div onClick={() => setServiceType(item)}>{item?.name}</div>,
    };
  });

  const statusItems = statusList?.map((item) => {
    return {
      key: item?.type,
      label: <div onClick={() => setStatusType(item)}>{item?.name}</div>,
    };
  });

  const getEmptyDiv = (isWeb: boolean = false) => {
    return (
      <div className={classNames([styles.cardEmptyItem, !isWeb && styles.phoneCardEmptyItem])}>
        <div className={styles.emptyTitle}>{t('Add an Account to Start')}</div>
        <p className={styles.emptyDesc}>
          {t(
            "You haven't added any Instagram accounts yet! Add one now to start exploring, tracking, and discovering valuable insights today."
          )}
        </p>
        <div
          className={styles.emptyBtn}
          onClick={() => {
            window.open(serviceType?.link, '_blank');
          }}
        >
          {t('Start Now')}
        </div>
      </div>
    );
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.phoneContainer}>
        <div className={styles.feeList}>
          {typeList.map((item, index) => {
            return (
              <div
                key={index}
                className={classNames([
                  styles.feeItem,
                  {
                    [styles.active]: serviceType?.type === item?.type,
                  },
                ])}
                onClick={() => {
                  setServiceType(item);
                }}
              >
                <Image src={item?.img} alt={''} width={25} height={25} />
                <span>{item?.name}</span>
              </div>
            );
          })}
        </div>
        {getShowList?.length > 0 ? (
          <div className={styles.cardList}>
            {[...getShowList]?.map((subscription, index) => (
              <div className={styles.cardItem} key={index}>
                <div className={styles.infoContainer}>
                  <div className={styles.infoItem}>
                    <Image src={serviceType?.img} alt="" width={24} height={24} />
                    <span>@{serviceType?.name}</span>
                  </div>

                  <span>
                    {subscription?.status == 'normal' ? (
                      <span
                        className="cursor-pointer hover:text-[#725CFF]"
                        onClick={() => {
                          setSelectRow(subscription);
                          toggleUnsubscribeStop(subscription as OrderInfoModel);
                          ga_logEvent(EventName.settings_Plan_Stop);
                        }}
                      >
                        {t('Cancel')}
                      </span>
                    ) : (
                      <span
                        className="cursor-pointer hover:text-[#725CFF]"
                        onClick={() => {
                          setSelectRow(subscription);
                          onResubscribeClick((subscription as OrderInfoModel)?.subs_id);
                          ga_logEvent(EventName.settings_Plan_Subscription);
                        }}
                      >
                        {t('Resume')}
                      </span>
                    )}
                  </span>
                </div>

                <div className={styles.cardContent}>
                  <div className={styles.userItem}>
                    <span>{subscription?.data_type}</span>
                    <span className={styles.status}>
                      <span
                        className={styles.circleSpan}
                        style={{
                          background: subscription.status === 'normal' ? '#38C13D' : '#CDCDCD',
                        }}
                      />
                      {subscription.status === 'normal' ? t('Active') : t('Cancelled')}
                    </span>
                  </div>
                  <div className={styles.expireTime}>
                    <span style={{ paddingRight: '4px' }}>
                      {subscription.status === 'normal' ? t('Your next billing date is') : t('Available before')}
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{subscription?.expire_time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          getEmptyDiv()
        )}
      </div>

      <div className={styles.subscriptionManager}>
        <h2 className={styles.title}>{t('Subscription')}</h2>
        <div className={styles.filters}>
          <AntdDropdown menu={{ items: typeItems }}>
            <span className={styles.filterSelect}>
              <span>{serviceType?.name}</span>
              <ChevronDown />
            </span>
          </AntdDropdown>

          <AntdDropdown menu={{ items: statusItems }}>
            <span className={styles.filterSelect}>
              <span>{statusType?.name}</span>
              <ChevronDown />
            </span>
          </AntdDropdown>
        </div>

        {getShowList?.length > 0 ? (
          <div className={styles.tableContent}>
            <table className={styles.subscriptionTable}>
              <thead>
                <tr>
                  <th>{t('Service Type')}</th>
                  <th>{t('Plan')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Date')}</th>
                  <th>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {getShowList?.map((subscription, index) => (
                  <tr key={index}>
                    <td>
                      <div className={styles.serviceType}>
                        {subscription?.media_name && <p>@{subscription?.media_name}</p>}
                        <span>{serviceType?.name}</span>
                      </div>
                    </td>
                    {subscription.status == 'trial' ? (
                      <td>{serviceType?.type === 'recent_follow' ? t('Referral Reward') : t('SHARING REWARD')}</td>
                    ) : (
                      <td>{subscription?.data_type}</td>
                    )}
                    <td>
                      {subscription?.status == 'trial' ? (
                        dayjs(subscription?.expire_time).isAfter(dayjs()) ? (
                          <span className={`${styles.status}`}>
                            <span
                              className={styles.circleSpan}
                              style={{
                                background: '#38C13D',
                              }}
                            />
                            {t('Active')}
                          </span>
                        ) : (
                          <span className={`${styles.status}`}>
                            <span
                              className={styles.circleSpan}
                              style={{
                                background: '#CDCDCD',
                              }}
                            />
                            {t('Expired')}
                          </span>
                        )
                      ) : (
                        <span className={`${styles.status}`}>
                          <span
                            className={styles.circleSpan}
                            style={{
                              background: subscription.status === 'normal' ? '#38C13D' : '#CDCDCD',
                            }}
                          />
                          {subscription.status === 'normal' ? t('Active') : t('Cancelled')}
                        </span>
                      )}
                    </td>

                    <td>
                      <div>
                        <span>
                          {subscription.status === 'normal'
                            ? t('Your next billing date is')
                            : subscription.status == 'trial'
                              ? t('Expires at')
                              : t('Available before')}
                        </span>
                        <br />
                        <span style={{ whiteSpace: 'nowrap' }}>{subscription?.expire_time}</span>
                      </div>
                    </td>

                    <td>
                      {subscription.status != 'trial' && (
                        <span>
                          {subscription?.status == 'normal' ? (
                            <span
                              className="cursor-pointer hover:text-[#725CFF]"
                              onClick={() => {
                                setSelectRow(subscription);
                                toggleUnsubscribeStop(subscription as OrderInfoModel);
                                ga_logEvent(EventName.settings_Plan_Stop);
                              }}
                            >
                              {t('Cancel Subscription')}
                            </span>
                          ) : (
                            <span
                              className="cursor-pointer hover:text-[#725CFF]"
                              onClick={() => {
                                setSelectRow(subscription);
                                onResubscribeClick((subscription as OrderInfoModel)?.subs_id);
                                ga_logEvent(EventName.settings_Plan_Subscription);
                              }}
                            >
                              {t('Resume Subscription')}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <SettingsEmptyContent
            title={getEmptyDiv(true)}
            // desction={t("It looks like you haven't started tracking any accounts yet! <a href='1xxx1' style='color:#725CFF;fontWeight:600'>Add an account</a> to unlock insights into someone's Instagram activity today!").replace('1xxx1', serviceType?.type === 'main' ? '/dashboard?alertType=1' : serviceType?.link )}
          />
        )}
      </div>

      {unsubscribeContent()}
      {unsubscribeContentStop()}
    </Spin>
  );
};

const SubscriptionContent: React.FC = () => {
  return <SubscriptionDetailContent />;
};

export default SubscriptionContent;
